import { router } from '@inertiajs/react'
import { Palette, MessageSquare } from 'lucide-react'
import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Label } from '@/components/ui/Label'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import { activeBrandParam } from '@/lib/activeBrand'
import { WidgetFeaturesSection, DEFAULT_WIDGET_FEATURES, type WidgetFeatureFlags } from './WidgetFeaturesSection'

interface ChannelConfig {
  primary_color: string
  position: string
  display: string
  greeting: string
  require_email: boolean
  require_first_name: boolean
  require_last_name: boolean
  require_topic: boolean
  show_logo_on_launcher: boolean
  auto_open_on_message: boolean
  features?: WidgetFeatureFlags
}

interface Channel {
  id: string
  config: ChannelConfig
}

interface WidgetSettingsCardProps {
  channel: Channel | null
}

export function WidgetSettingsCard({ channel }: WidgetSettingsCardProps) {
  const { withAccount } = useRouteParams()
  const [widgetConfig, setWidgetConfig] = useState<ChannelConfig>(channel?.config ?? {
    primary_color: '#10b981',
    position: 'bottom-right',
    display: 'popup',
    greeting: 'Hi there! How can we help?',
    require_email: false,
    require_first_name: false,
    require_last_name: false,
    require_topic: false,
    show_logo_on_launcher: false,
    auto_open_on_message: false
  })

  // Config may omit flags entirely (older channels have no features key at all),
  // so defaults fill the gaps the same way the widget itself does.
  const features: WidgetFeatureFlags = { ...DEFAULT_WIDGET_FEATURES, ...(widgetConfig.features ?? {}) }

  const handleFeatureChange = (key: keyof WidgetFeatureFlags, value: boolean) => {
    setWidgetConfig(prev => ({
      ...prev,
      features: { ...DEFAULT_WIDGET_FEATURES, ...(prev.features ?? {}), [key]: value },
    }))
  }

  const handleWidgetConfigChange = (key: keyof ChannelConfig, value: string | boolean) => {
    setWidgetConfig(prev => ({ ...prev, [key]: value }))
  }

  const saveWidgetSettings = () => {
    router.patch(routes.settings.updateWidget.path(withAccount()), {
      widget: {
        primary_color: widgetConfig.primary_color,
        position: widgetConfig.position,
        display: widgetConfig.display,
        greeting: widgetConfig.greeting,
        require_email: widgetConfig.require_email,
        require_first_name: widgetConfig.require_first_name,
        require_last_name: widgetConfig.require_last_name,
        require_topic: widgetConfig.require_topic,
        show_logo_on_launcher: widgetConfig.show_logo_on_launcher,
        auto_open_on_message: widgetConfig.auto_open_on_message,
        features: { ...features },
      },
      channel_id: channel?.id,
      ...activeBrandParam(),
    }, {
      preserveScroll: true
    })
  }

  if (!channel) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="size-5" />
          Widget Settings
        </CardTitle>
        <CardDescription>
          Configure your chat widget appearance and visitor requirements
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
          Colours and logo come from <span className="font-medium text-foreground">Product &amp; Brand</span> and are
          applied to this widget automatically.
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-base">Display Mode</Label>
            <p className="text-sm text-muted-foreground mt-1">
              Choose how the widget appears on your website
            </p>
          </div>
          <div className="space-y-2">
            <Select
              value={widgetConfig.display}
              onValueChange={(value) => handleWidgetConfigChange('display', value)}
            >
              <SelectTrigger className="w-48 cursor-pointer">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="popup" className="cursor-pointer">Popup</SelectItem>
                <SelectItem value="drawer" className="cursor-pointer">Drawer</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {widgetConfig.display === 'drawer'
                ? 'Widget appears as a full-height side panel. The launcher button is hidden.'
                : 'Widget appears as a floating popup triggered by a launcher button.'}
            </p>
            <p className="text-sm text-muted-foreground">
              This is the default for every page. To use a different mode on some pages (for example popup on your login page, drawer once signed in), call{' '}
              <code className="bg-muted px-1 rounded">banshee('setDisplay', 'drawer')</code> or{' '}
              <code className="bg-muted px-1 rounded">banshee('setDisplay', 'popup')</code>. No page reload is needed.
            </p>
          </div>
        </div>

        <div className="space-y-4 pt-4 border-t border-border">
          <div>
            <Label className="text-base">Launcher Appearance</Label>
            <p className="text-sm text-muted-foreground mt-1">
              Customize how the chat launcher button appears on your site
            </p>
          </div>
          <div className="space-y-3">
            <Checkbox
              id="show_logo_on_launcher"
              label="Show account logo on launcher bubble"
              checked={widgetConfig.show_logo_on_launcher}
              onCheckedChange={(checked) => handleWidgetConfigChange('show_logo_on_launcher', checked === true)}
            />
            <p className="text-sm text-muted-foreground ml-6 -mt-2">
              Display your account logo instead of the default chat icon
            </p>
          </div>
        </div>

        <div className="space-y-4 pt-4 border-t border-border">
          <div>
            <Label className="text-base">Notifications</Label>
            <p className="text-sm text-muted-foreground mt-1">
              Configure how visitors are notified of new messages
            </p>
          </div>
          <div className="space-y-3">
            <Checkbox
              id="auto_open_on_message"
              label="Auto-open widget on new message"
              checked={widgetConfig.auto_open_on_message}
              onCheckedChange={(checked) => handleWidgetConfigChange('auto_open_on_message', checked === true)}
            />
            <p className="text-sm text-muted-foreground ml-6 -mt-2">
              Automatically open the chat widget when an agent sends a message
            </p>
          </div>
        </div>

        <WidgetFeaturesSection features={features} onChange={handleFeatureChange} />

        <div className="space-y-4 pt-4 border-t border-border">
          <div>
            <Label className="text-base">Visitor Requirements</Label>
            <p className="text-sm text-muted-foreground mt-1">
              Require visitors to provide information before starting a chat
            </p>
          </div>
          <div className="space-y-3">
            <Checkbox
              id="require_email"
              label="Require email address"
              checked={widgetConfig.require_email}
              onCheckedChange={(checked) => handleWidgetConfigChange('require_email', checked === true)}
            />
            <Checkbox
              id="require_first_name"
              label="Require first name"
              checked={widgetConfig.require_first_name}
              onCheckedChange={(checked) => handleWidgetConfigChange('require_first_name', checked === true)}
            />
            <Checkbox
              id="require_last_name"
              label="Require last name"
              checked={widgetConfig.require_last_name}
              onCheckedChange={(checked) => handleWidgetConfigChange('require_last_name', checked === true)}
            />
          </div>
        </div>

        <div className="pt-4">
          <Button onClick={saveWidgetSettings} className="cursor-pointer">
            <Palette className="size-4 mr-2" />
            Save Widget Settings
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
